import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/context/SettingsContext';

const useKeyboardNavigation = () => {
    const navigate = useNavigate();
    const { settings } = useSettings();
    
    const defaultShortcuts = {
        save: { key: 's', ctrlKey: true },
        print: { key: 'p', ctrlKey: true },
        new_sale: { key: 'n', ctrlKey: true },
        new_item: { key: 'i', ctrlKey: true },
        search: { key: 'f', ctrlKey: true }
    };

    const shortcuts = settings?.shortcuts || defaultShortcuts;

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Helper to check if event matches a shortcut config
            const isMatch = (config) => {
                if (!config || typeof config !== 'object') return false;
                if (!e.key) return false;
                
                // Allow both Ctrl and Meta (Cmd) for 'ctrlKey' for platform friendliness
                const hasCtrl = e.ctrlKey || e.metaKey;
                const targetCtrl = !!config.ctrlKey || !!config.metaKey;
                
                return (
                    e.key.toLowerCase() === config.key?.toLowerCase() &&
                    hasCtrl === targetCtrl &&
                    !!e.altKey === !!config.altKey &&
                    !!e.shiftKey === !!config.shiftKey
                );
            };

            // 1. Handle Save Shortcut
            if (isMatch(shortcuts.save)) {
                e.preventDefault();
                
                // Priority 1: If there's an explicit "save" button in the DOM, click it
                const saveBtn = document.querySelector('[data-shortcut="save"]');
                if (saveBtn && !saveBtn.disabled) {
                    saveBtn.click();
                } else {
                    // Priority 2: Dispatch a custom event for components that handle save logic without a dedicated button
                    const event = new CustomEvent('app-shortcut-save');
                    window.dispatchEvent(event);
                }
                return;
            }

            // 2. Handle Print Shortcut
            if (isMatch(shortcuts.print)) {
                e.preventDefault();
                const printBtn = document.querySelector('[data-shortcut="print"]');
                if (printBtn && !printBtn.disabled) {
                    printBtn.click();
                } else {
                    window.dispatchEvent(new CustomEvent('app-shortcut-print'));
                }
                return;
            }

            // 3. Handle New Sale Shortcut
            if (isMatch(shortcuts.new_sale)) {
                e.preventDefault();
                navigate('/sales/new');
                return;
            }

            // 3. Handle New Item Shortcut
            if (isMatch(shortcuts.new_item)) {
                e.preventDefault();
                navigate('/item-master?action=new');
                return;
            }

            // 4. Handle Search Shortcut
            if (isMatch(shortcuts.search)) {
                e.preventDefault();
                const searchInput = document.getElementById('qr-search-input');
                if (searchInput) {
                    searchInput.focus();
                    searchInput.select();
                } else {
                    // Dispatch event for specialized search handlers
                    window.dispatchEvent(new CustomEvent('app-shortcut-search'));
                }
                return;
            }

            // 5. Handle Alt + 1-9 for Bookmarks
            if (e.altKey && e.key >= '1' && e.key <= '9') {
                e.preventDefault();
                try {
                    const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
                    const index = parseInt(e.key) - 1;
                    if (bookmarks[index] && bookmarks[index].path) {
                        navigate(bookmarks[index].path);
                    }
                } catch (err) {
                    console.error('Failed to parse bookmarks:', err);
                }
                return;
            }

            // 2. Handle Enter and Arrow Keys for Focus Management
            const isEnter = e.key === 'Enter';
            const isArrow = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);

            if (isEnter || isArrow) {
                // If another listener already handled this (like in TransactionForm), skip
                if (e.defaultPrevented) return;

                const activeElement = document.activeElement;
                
                // Exclude elements where Enter/Arrows should behave normally
                const isTextArea = activeElement.tagName === 'TEXTAREA';
                const isSelect = activeElement.tagName === 'SELECT';
                const isButton = activeElement.tagName === 'BUTTON';
                
                // Special case for Select triggers (Radix/Shadcn)
                const isSelectTrigger = activeElement.hasAttribute('role') && activeElement.getAttribute('role') === 'combobox';
                
                if (isTextArea) return; 
                if ((isSelect || isSelectTrigger) && isArrow) return;

                // Elements we want to navigate between
                const selector = 'input:not([type="hidden"]), select, button:not([disabled]), [tabindex="0"]:not([tabindex="-1"])';
                const focusableElements = Array.from(document.querySelectorAll(selector)).filter(el => {
                    const style = window.getComputedStyle(el);
                    return !el.disabled && 
                           style.display !== 'none' && 
                           style.visibility !== 'hidden' &&
                           el.offsetWidth > 0 && el.offsetHeight > 0;
                });

                const currentIndex = focusableElements.indexOf(activeElement);
                if (currentIndex === -1) return;

                let nextIndex = -1;

                if (isEnter) {
                    // Don't prevent default on buttons, let them click
                    if (isButton) return;
                    
                    e.preventDefault();
                    nextIndex = currentIndex + 1;
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    nextIndex = currentIndex + 1;
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    nextIndex = currentIndex - 1;
                } else if (e.key === 'ArrowRight' && activeElement.tagName === 'INPUT') {
                    // Only move to next if cursor is at the end
                    if (activeElement.selectionEnd === activeElement.value.length) {
                        e.preventDefault();
                        nextIndex = currentIndex + 1;
                    }
                } else if (e.key === 'ArrowLeft' && activeElement.tagName === 'INPUT') {
                    // Only move to prev if cursor is at the beginning
                    if (activeElement.selectionStart === 0) {
                        e.preventDefault();
                        nextIndex = currentIndex - 1;
                    }
                }

                if (nextIndex >= 0 && nextIndex < focusableElements.length) {
                    const nextEl = focusableElements[nextIndex];
                    nextEl.focus();
                    if (nextEl.tagName === 'INPUT' && typeof nextEl.select === 'function') {
                        nextEl.select(); // Professional ERP behavior: select all text on focus
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);
};

export default useKeyboardNavigation;
